import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  ORGANIZATION_SEARCH_PAGE_SIZE,
} from '../constants';
import type {
  OrganizationRecord,
  OrganizationTotalsParams,
  PagedResult,
} from '../types/index';
import { findOrganizationByExactName } from '../utils/organizationLookup';
import { throwIfLegacyResultError } from '../utils/requestErrors';

export type OrganizationFacilitiesAndEmailsReportRecord = {
  id: number;
  name: string;
  facilities: Array<{
    id: number;
    name: string;
    license: string;
  }>;
  emailAddresses: Array<{
    emailAddress: string;
  }>;
};

type UseOrganizationsParams = OrganizationTotalsParams & {
  enabled?: boolean;
};

type UseOrganizationsResult = {
  data: OrganizationRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
  total: number;
};

export type OrganizationOption = Pick<OrganizationRecord, 'id' | 'name'>;

type OrganizationOptionsParams = {
  enabled?: boolean;
  name?: string | null;
  page?: number;
  pageSize?: number;
};

type UseOrganizationFacilitiesAndEmailsReportParams = {
  enabled?: boolean;
  sortField?: string | null;
  sortAscending?: boolean | null;
};

type UseOrganizationFacilitiesAndEmailsReportResult = {
  data: OrganizationFacilitiesAndEmailsReportRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

export async function getOrganizations({
  page = DEFAULT_LIST_PAGE,
  pageSize = 50,
  name = null,
}: OrganizationTotalsParams = {}): Promise<OrganizationRecord[]> {
  const response = await axios.get('/organization', {
    params: { page, pageSize, name },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getOrganizationsTotals({
  isQuickstart = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  name = null,
  sortField = null,
  sortAscending = null,
}: OrganizationTotalsParams = {}): Promise<PagedResult<OrganizationRecord>> {
  const params = {
    ...(isQuickstart === null || isQuickstart === undefined ? {} : { isQuickstart }),
    page,
    pageSize,
    name,
    ...(sortField === null || sortField === undefined ? {} : { sortField }),
    ...(sortAscending === null || sortAscending === undefined ? {} : { sortAscending }),
  };
  const response = await axios.get('/organization/totals', {
    params,
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: Array.isArray(response.data?.data) ? response.data.data : [],
  };
}

export async function getOrganizationByExactName(
  requestedName: unknown,
): Promise<OrganizationRecord | null> {
  return findOrganizationByExactName<OrganizationRecord>(getOrganizationsTotals, requestedName);
}

export async function getOrganizationsAggregateTotals({
  isQuickstart = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  name = null,
  sortField = null,
  sortAscending = null,
}: OrganizationTotalsParams = {}): Promise<PagedResult<OrganizationRecord>> {
  const response = await axios.get('/organization/totals/aggregate', {
    params: {
      ...(isQuickstart === null || isQuickstart === undefined ? {} : { isQuickstart }),
      page,
      pageSize,
      name,
      ...(sortField === null || sortField === undefined ? {} : { sortField }),
      ...(sortAscending === null || sortAscending === undefined ? {} : { sortAscending }),
    },
  });
  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: Array.isArray(response.data?.data) ? response.data.data : [],
  };
}

export async function mergeQuickstartOrganization(payload: {
  sourceQuickstartOrganizationId: number | string;
  targetOrganizationId: number | string;
}): Promise<any> {
  const response = await axios.post('/organization/merge-quickstart', payload);
  return response.data;
}

export async function mergeOrganization(payload: {
  sourceOrganizationId: number | string;
  targetOrganizationId: number | string;
}): Promise<any> {
  const response = await axios.post('/organization/merge', payload);
  return response.data;
}

export async function getOrganizationById(organizationId: number | string): Promise<OrganizationRecord | null> {
  const response = await axios.get('/organization/options/organization', {
    params: { organizationId },
  });
  return response.data && typeof response.data === 'object' ? response.data : null;
}

export async function getOrganizationOptions({
  page = DEFAULT_LIST_PAGE,
  pageSize = ORGANIZATION_SEARCH_PAGE_SIZE,
  name = null,
}: Omit<OrganizationOptionsParams, 'enabled'> = {}): Promise<PagedResult<OrganizationOption>> {
  const response = await axios.get('/organization/options', {
    params: { page, pageSize, name },
  });
  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: Array.isArray(response.data?.data) ? response.data.data : [],
  };
}

export function useOrganizationById(
  organizationId: number | string | null | undefined,
  enabled = true,
) {
  const [data, setData] = useState<OrganizationRecord | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled && organizationId));
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled || !organizationId) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setError(null);
    setData(null);
    setIsLoading(true);
    try {
      const result = await getOrganizationById(organizationId);
      if (requestId === requestIdRef.current) setData(result);
    } catch (nextError) {
      if (requestId === requestIdRef.current) {
        setData(null);
        setError(nextError);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    void refetch();
    return () => { requestIdRef.current += 1; };
  }, [refetch]);

  return { data, error, isLoading, refetch };
}

export function useOrganizationOptions({
  enabled = true,
  name = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = ORGANIZATION_SEARCH_PAGE_SIZE,
}: OrganizationOptionsParams = {}) {
  const [result, setResult] = useState<PagedResult<OrganizationOption>>({ page, pageSize, total: 0, data: [] });
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled) {
      setResult({ page, pageSize, total: 0, data: [] });
      setError(null);
      setIsLoading(false);
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const nextResult = await getOrganizationOptions({ name, page, pageSize });
      if (requestId === requestIdRef.current) setResult(nextResult);
    } catch (nextError) {
      if (requestId === requestIdRef.current) {
        setResult({ page, pageSize, total: 0, data: [] });
        setError(nextError);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [enabled, name, page, pageSize]);

  useEffect(() => {
    void refetch();
    return () => { requestIdRef.current += 1; };
  }, [refetch]);

  return { ...result, error, isLoading, refetch };
}

export async function addOrganization(name: string): Promise<OrganizationRecord> {
  const response = await axios.post('/organization/add', { name });
  return throwIfLegacyResultError(response.data, 'Organization could not be saved.');
}

export async function editOrganization(organizationId: number | string, name: string): Promise<unknown> {
  const response = await axios.post('/organization/edit', { organizationId, name });
  return response.data;
}

export async function getOrganizationFacilitiesAndEmailsReport({
  sortField = null,
  sortAscending = null,
}: {
  sortField?: string | null;
  sortAscending?: boolean | null;
} = {}): Promise<OrganizationFacilitiesAndEmailsReportRecord[]> {
  const response = await axios.get('/reports/facilities-and-emails', {
    params: { sortField, sortAscending },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export function useOrganizationFacilitiesAndEmailsReport({
  enabled = true,
  sortField = null,
  sortAscending = null,
}: UseOrganizationFacilitiesAndEmailsReportParams = {}): UseOrganizationFacilitiesAndEmailsReportResult {
  const [data, setData] = useState<OrganizationFacilitiesAndEmailsReportRecord[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!enabled) {
      setData([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const results = await getOrganizationFacilitiesAndEmailsReport({
        sortField,
        sortAscending,
      });
      if (requestId === requestIdRef.current) {
        setData(results);
      }
    } catch (nextError) {
      if (requestId === requestIdRef.current) {
        setError(nextError);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [enabled, sortAscending, sortField]);

  useEffect(() => {
    void refetch();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refetch]);

  return {
    data,
    error,
    isLoading,
    refetch,
  };
}

export default function useOrganizations({
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  name = null,
  enabled = true,
}: UseOrganizationsParams = {}): UseOrganizationsResult {
  const [data, setData] = useState<OrganizationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled) {
      setData([]);
      setTotal(0);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await getOrganizationsTotals({ page, pageSize, name });
      if (requestId === requestIdRef.current) {
        setData(results.data);
        setTotal(results.total);
      }
    } catch (nextError) {
      if (requestId === requestIdRef.current) {
        setError(nextError);
        setData([]);
        setTotal(0);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [enabled, name, page, pageSize]);

  useEffect(() => {
    void refetch();
    return () => { requestIdRef.current += 1; };
  }, [refetch]);

  return {
    data,
    error,
    isLoading,
    refetch,
    total,
  };
}
